import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import StaticInput from '../elements/forms/StaticInput'
import TextInput from '../elements/forms/TextInput'
import GenericSelect from '../elements/forms/GenericSelect'
import MultiSelect from '../elements/forms/MultiSelect'
import Dismissible from '../elements/Dismissible'
import AppConstants from '../../utils/AppConstants'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_NAME: 'Veuillez saisir un nom pour ce groupe.',
  EMPTY_LOCATIONS: 'Vous devez ajouter au moins un emplacement pour créer un groupe.',
  EMPTY_MANAGER: 'Veuillez attribuer un responsable à ce groupe.'
}

class ControlGroupForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: []
    }

    // Bind events
    this.onSubmit = this.onSubmit.bind(this)
    this.onChangeInputName = this.onChangeInputName.bind(this)
    this.onChangeInputLocations = this.onChangeInputLocations.bind(this)
    this.onChangeInputManager = this.onChangeInputManager.bind(this)
    this.onChangeInputHelpers = this.onChangeInputHelpers.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }
  }

  getInitialInput(props = this.props) {
    return {
      name: (props.data && props.data.name) || '',
      manager_id: (props.data && props.data.manager.id) || null,
      locations: (props.data && _.map(
        props.data.locations,
        (location) => (location.id)
      )) || [],
      helpers: (props.data && _.map(
        props.data.helpers,
        (helper) => (helper.id)
      )) || []
    }
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  clearError(errorToClear) {
    this.setState((prevState, props) => ({
      formErrors: _.filter(
        prevState.formErrors,
        (error) => (error !== errorToClear)
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (!_.isString(this.state.input.name) || this.state.input.name.length === 0) {
      formErrors.push(ERROR.EMPTY_NAME)
    }
    if (this.state.input.locations.length === 0) {
      formErrors.push(ERROR.EMPTY_LOCATIONS)
    }
    if (_.isNull(this.state.input.manager_id)) {
      formErrors.push(ERROR.EMPTY_MANAGER)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors)) {
      return;
    }

    // Build and submit correctly formatted input
    const input = _.extend(
      {},
      _.pick(this.state.input, 'name'),
      {
        manager: {
          id: this.state.input.manager_id
        },
        locations: _.map(
          this.state.input.locations,
          (id) => ({ id })
        ),
        helpers: _.map(
          this.state.input.helpers,
          (id) => ({ id })
        )
      },
      this.props.data
        ? {}
        : {
            control: {
              id: this.props.control.id
            }
          }
    )
    this.props.onSubmit(input)
  }

  onChangeInputName(name) {
    this.setInput({ name })
    this.clearError(ERROR.EMPTY_NAME)
  }

  onChangeInputLocations(locations) {
    this.setInput({ locations })
    this.clearError(ERROR.EMPTY_LOCATIONS)
  }

  onChangeInputManager(manager_id) {
    this.setInput({ manager_id })
    this.clearError(ERROR.EMPTY_MANAGER)
  }

  onChangeInputHelpers(helpers) {
    this.setInput({ helpers })
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error, {
                  [AppConstants.ERROR.BAD_REQUEST.UNICITY_CONSTRAINT_VIOLATION]: 'Un groupe avec le même nom existe déjà dans ce contrôle.'
                })}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          <StaticInput
            label='Contrôle'
            content={this.props.control.name}
          />
          <TextInput
            label='Groupe'
            placeholder='Villages...'
            value={this.state.input.name}
            onChange={this.onChangeInputName}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_NAME)
                ? ERROR.EMPTY_NAME
                : null
            }
          />
          <MultiSelect
            label='Emplacements'
            loading={this.props.locationsIsFetching}
            options={
              _.isArray(this.props.locationsList)
                ? this.props.locationsList
                : []
            }
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_LOCATIONS)
                ? ERROR.EMPTY_LOCATIONS
                : null
            }
            selectedKeys={this.state.input.locations}
            onChangeSelectedKeys={this.onChangeInputLocations}
            forbiddenKeys={
              _.isArray(this.props.locationsAlreadyused)
                ? _.map(
                    this.props.locationsAlreadyused,
                    (location) => (location.id)
                  )
                : []
            }
            forbiddenMessage="Cet emplacement fait déjà partie d'un autre groupe"
            isLocations={true}
          />
          <GenericSelect
            label="Responsable"
            loading={this.props.usersIsFetching}
            options={
              _.isArray(this.props.usersList)
                ? _.map(
                    _.filter(
                      this.props.usersList,
                      (user) => (!_.contains(this.state.input.helpers, user.id))
                    ),
                    (user) => ({
                      key: user.id,
                      value: user.name
                    })
                  )
                : []
            }
            selected={this.state.input.manager_id}
            onChange={this.onChangeInputManager}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_MANAGER)
                ? ERROR.EMPTY_MANAGER
                : null
            }
            canSearch={true}
          />
          <MultiSelect
            label='Aides'
            loading={this.props.usersIsFetching}
            options={
              _.isArray(this.props.usersList)
                ? _.map(
                    this.props.usersList,
                    (user) => ({
                      key: user.id,
                      value: user.name
                    })
                  )
                : []
            }
            error={null}
            selectedKeys={this.state.input.helpers}
            onChangeSelectedKeys={this.onChangeInputHelpers}
            forbiddenKeys={
              _.isNull(this.state.input.manager_id)
                ? []
                : [ this.state.input.manager_id ]
            }
            forbiddenMessage="Le responsable du groupe ne peut pas être ajouté également comme aide."
            isLocations={false}
          />
        </Form>
      </React.Fragment>
    )
  }
}

ControlGroupForm.propTypes = {
  control: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  error: PropTypes.object,
  locationsList: PropTypes.array,
  locationsIsFetching: PropTypes.bool.isRequired,
  locationsAlreadyused: PropTypes.array,
  usersList: PropTypes.array,
  usersIsFetching: PropTypes.bool.isRequired
}

export default ControlGroupForm



// WEBPACK FOOTER //
// ./src/components/forms/ControlGroupForm.js